import { useEffect, useState } from "react";
import { Button, Loader, TextInput, UnstyledButton } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconCheck, IconChevronDown, IconDiscount2, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment-jalaali";
import { DiscountDto, DiscountType } from "@/models/club";
import { discountService } from "@/services/discount";
import classes from "./invoiceDiscount.module.css";

// What the invoice uses: the code and the amount the server will take off. `pending` while the preview is
// out of date (the amount owed just changed), so the invoice is not sent with an old discount.
export type AppliedDiscount = { code: string | null; amount: number; pending: boolean };

const rial = (value?: number | null) => `${Math.round(value ?? 0).toLocaleString()} ریال`;

function describe(discount: DiscountDto) {
  const value =
    discount.discountType === DiscountType.Value1
      ? `${discount.percent ?? 0}٪${discount.maximumAmount ? ` تا سقف ${rial(discount.maximumAmount)}` : ""}`
      : rial(discount.amount);
  const left = Math.max(0, (discount.count ?? 0) - (discount.useCount ?? 0));
  const notes = [
    discount.minimumAmount ? `حداقل خرید ${rial(discount.minimumAmount)}` : null,
    discount.expireDate ? `تا ${moment(discount.expireDate).format("jYYYY/jMM/jDD")}` : null,
    left > 0 ? `${left.toLocaleString()} بار باقی‌مانده` : null,
  ].filter(Boolean);
  return { value, notes: notes.join(" · ") };
}

// The invoice's discount code (frontend guide: invoice-discount-frontend.md). The discount is taken off what is
// still owed after the deposit; Validate is only a preview, the code is used when the invoice is saved.
// `collapsible`: starts as one row («کد تخفیف · ۲ کد فعال») and opens on demand, so the summary stays short.
export default function InvoiceDiscount({ customerId, owed, onChange, collapsible = false }: { customerId?: number; owed: number; onChange: (applied: AppliedDiscount) => void; collapsible?: boolean }) {
  const [code, setCode] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [typed, setTyped] = useState("");
  const [owedSettled] = useDebouncedValue(owed, 400);

  // A code belongs to one customer: a different customer starts without one.
  useEffect(() => {
    setCode(null);
    setTyped("");
    setOpened(false);
  }, [customerId]);

  const list = useQuery({
    queryKey: ["getCustomerDiscounts", customerId],
    queryFn: () => discountService.getCustomerDiscounts(customerId!),
    enabled: !!customerId,
    staleTime: 30_000,
  });
  const usable = (list.data ?? []).filter((discount) => discount.isValid && discount.code);

  const canPreview = !!customerId && !!code && owedSettled > 0;
  const preview = useQuery({
    queryKey: ["invoiceDiscountPreview", customerId, code, owedSettled],
    queryFn: () => discountService.validateDiscountByPurchase({ customerId: customerId!, discountCode: code!, purchaseAmount: owedSettled }),
    enabled: canPreview,
    retry: false,
    staleTime: 0,
  });
  const result = canPreview ? preview.data : undefined;
  const pending = !!code && (owed !== owedSettled || (canPreview && preview.isFetching));
  const amount = result?.isValid ? Math.min(result.amount ?? 0, owedSettled) : 0;

  useEffect(() => {
    onChange({ code: result?.isValid ? code : null, amount, pending });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, amount, pending, result?.isValid]);

  const choose = (next: string | null) => setCode((current) => (current === next ? null : next));
  const applyTyped = () => {
    const value = typed.trim();
    if (value) setCode(value);
  };

  const open = !collapsible || opened || !!code;
  const head = (count?: number) =>
    collapsible ? (
      <button type="button" className={classes.toggle} aria-expanded={open} onClick={() => setOpened(!open)} disabled={!!code}>
        <Head count={count} />
        {!code && <IconChevronDown size={18} className={classes.chevron} data-open={open} aria-hidden="true" />}
      </button>
    ) : (
      <Head count={count} />
    );

  if (!customerId)
    return (
      <section className={classes.box} aria-label="کد تخفیف">
        {head()}
        {open && <p className={classes.muted}>برای استفاده از کد تخفیف، اول مشتری را انتخاب کنید.</p>}
      </section>
    );

  return (
    <section className={classes.box} aria-label="کد تخفیف">
      {head(usable.length)}
      {!open ? null : owed <= 0 && !code ? (
        <p className={classes.muted}>مبلغی برای تخفیف باقی نمانده است.</p>
      ) : (
        <>
          {list.isLoading ? (
            <Loader size="xs" color="violet" />
          ) : usable.length > 0 ? (
            <div className={classes.codes} role="radiogroup" aria-label="کدهای تخفیف این مشتری">
              {usable.map((discount) => {
                const { value, notes } = describe(discount);
                const selected = code === discount.code;
                return (
                  <UnstyledButton key={discount.code} role="radio" aria-checked={selected} data-selected={selected} className={classes.code} onClick={() => choose(discount.code!)}>
                    <span className={classes.codeHead}>
                      <strong dir="ltr">{discount.code}</strong>
                      <span className={classes.value}>{value}</span>
                      <span className={classes.check} aria-hidden="true">{selected && <IconCheck size={13} stroke={3} />}</span>
                    </span>
                    {notes && <span className={classes.notes}>{notes}</span>}
                  </UnstyledButton>
                );
              })}
            </div>
          ) : (
            <p className={classes.muted}>این مشتری کد تخفیف فعالی ندارد؛ اگر کدی دارد وارد کنید.</p>
          )}

          <div className={classes.manual}>
            <TextInput
              aria-label="کد تخفیف"
              placeholder="وارد کردن کد"
              value={typed}
              onChange={(event) => setTyped(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  applyTyped();
                }
              }}
              maxLength={50}
              styles={{ input: { direction: "ltr", textAlign: "left", fontFamily: typed ? "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" : undefined } }}
              style={{ flex: 1 }}
            />
            <Button variant="light" color="violet" onClick={applyTyped} disabled={!typed.trim()}>اعمال</Button>
          </div>

          {code && (
            <div className={classes.result} data-state={pending ? "pending" : result?.isValid ? "ok" : result ? "error" : "pending"} aria-live="polite">
              {pending || !result ? (
                <><Loader size={14} color="violet" /><span>در حال بررسی کد <span dir="ltr" className={classes.mono}>{code}</span>…</span></>
              ) : result.isValid ? (
                <><IconCheck size={16} /><span>{rial(amount)} تخفیف با کد <b dir="ltr" className={classes.mono}>{code}</b></span></>
              ) : (
                <span>{result.message || "این کد برای این فاکتور قابل استفاده نیست."}</span>
              )}
              <button type="button" className={classes.remove} aria-label="حذف کد تخفیف" onClick={() => setCode(null)}><IconX size={15} /></button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Head({ count }: { count?: number }) {
  return (
    <div className={classes.head}>
      <IconDiscount2 size={18} aria-hidden="true" />
      <strong>کد تخفیف</strong>
      {!!count && <span className={classes.count}>{count.toLocaleString()} کد فعال</span>}
    </div>
  );
}
